/**
 * Human-readable summary of a finished catalog-refresh run.
 * Shared by the idle panel and the once-per-run toast so both say the same thing.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  CatalogRefreshOutcome,
  CatalogRefreshProgress,
} from '@/hooks/smswiser/catalog/refresh-job';

export type OutcomeTone = 'success' | 'warning' | 'error';

const STATUS_LABEL: Record<string, string> = {
  done: 'Completed',
  cancelled: 'Cancelled',
  timeout: 'Timed out',
  error: 'Failed',
  interrupted: 'Interrupted (server restarted)',
};

export function formatDurationMs(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest > 0 ? `${minutes}m ${rest}s` : `${minutes}m`;
}

export function outcomeStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/**
 * lastOutcome.succeeded/failed are absent when the run was cut short before it could
 * count (e.g. a redeploy) — the backend then derives the same window from the catalog
 * table into `progress`, so fall back to that instead of showing zeros.
 */
export function outcomeCounts(
  outcome: CatalogRefreshOutcome,
  progress: CatalogRefreshProgress,
): { succeeded: number; failed: number } {
  return {
    succeeded: outcome.succeeded ?? progress.done,
    failed: outcome.failed ?? progress.failed,
  };
}

export function outcomeTone(outcome: CatalogRefreshOutcome, failed: number): OutcomeTone {
  if (outcome.status === 'done') return failed > 0 ? 'warning' : 'success';
  if (outcome.status === 'cancelled') return 'warning';
  return 'error';
}

export function outcomeSummaryText(
  outcome: CatalogRefreshOutcome,
  progress: CatalogRefreshProgress,
): string {
  const { succeeded, failed } = outcomeCounts(outcome, progress);
  const base = `${succeeded} countries refreshed, ${failed} failed · ${formatDurationMs(outcome.durationMs)}`;
  return outcome.error ? `${base} · ${outcome.error}` : base;
}
